import type Toast from '../toast/Toast.js'
import { html, LitElement } from 'lit'

import { customElement } from 'lit/decorators.js'
import componentStyle from '../common/styles/Component.css'
import style from './ToastGroup.css'

type ToastOptions = Partial<Pick<Toast, 'variant' | 'autoDismiss'>>

/**
 * Toast group is used to position and style a group of toasts, whilst ensuring they are announced by screen readers.
 *
 * @status ready
 * @category feedback
 * @slot - Default slot in which to place toasts.
 *
 * @fires {NordEvent} dismiss - Fired when a toast is dismissed (via user action or auto-dismiss), and its exit animation has completed. This event should be used to remove the dismissed toast from the DOM.
 *
 * @parent Toast
 */
@customElement('nord-toast-group')
export default class ToastGroup extends LitElement {
  static styles = [componentStyle, style]

  disconnectedCallback(): void {
    super.disconnectedCallback()

    this.leaveTopLayer()
  }

  render() {
    return html`
      <div class="n-toast-group" role="log" aria-relevant="additions">
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
    `
  }

  private handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement
    const hasToasts = slot.assignedElements().length > 0

    if (hasToasts) {
      this.enterTopLayer()
    }
    else {
      this.leaveTopLayer()
    }
  }

  /**
   * Place the group in the top layer via the Popover API so that toasts render
   * above modals, drawers and other content regardless of stacking contexts
   * created by container queries, transforms or other containing block ancestors.
   *
   * Unlike popouts, dropdowns and tooltips, the toast group deliberately does
   * NOT listen for `DismissOverlaysEvent` (`nord-dismiss-overlays`): a toast is
   * meant to stay for its duration even when a modal opens, mirroring its
   * existing exclusion from `ModalController`'s focus-trap/`inert` handling.
   */
  private enterTopLayer() {
    if (typeof this.showPopover !== 'function' || this.matches(':popover-open'))
      return

    try {
      this.setAttribute('popover', 'manual')
      this.showPopover()
    }
    catch {
      this.removeAttribute('popover')
    }
  }

  private leaveTopLayer() {
    this.removeAttribute('popover')
  }

  /**
   * Convenience method for creating and adding a toast to the group.
   * @param {string} text - The text/message of the toast.
   * @param options - An optional object for configuring the toast's `variant` and `autoDismiss`.
   * @returns {Toast} The toast instance.
   */
  addToast(text: string, options: ToastOptions = {}) {
    const { variant, autoDismiss } = options
    const toast = document.createElement('nord-toast')

    if (variant) {
      toast.variant = variant
    }

    if (autoDismiss != null) {
      toast.autoDismiss = autoDismiss
    }

    toast.textContent = text
    this.appendChild(toast)

    return toast
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-toast-group': ToastGroup
  }
}
